/**
 * The rules a dash attack obeys (`StageMotionV1` `DASH`), written once so the canonical validator and the skill language's
 * checker refuse exactly the same dashes with exactly the same codes.
 *
 * A dash stage's member moves the caster toward its goal (at most `rangeM`) and its application, a skill-owned instant
 * circle at the caster, strikes where the caster landed. The caster is one body: it moves at most once per tick per cast,
 * so any program in which two dash members of one cast can release on the same tick is refused here rather than resolved
 * at run time.
 */
import { COMMON_DASH_MAX_DISTANCE_M } from '../sim/common-dash.js';
import { PLAYER_RADIUS_M } from '../sim/positioning.js';
import type { StageTimingV1 } from './composite-types.js';

/** PROVISIONAL: the longest dash a member makes, the common dash's distance; past it a dash is a different movement skill. */
export const DASH_ATTACK_RANGE_MAX_M = COMMON_DASH_MAX_DISTANCE_M;

/**
 * The smallest strike radius: an unshortened landing stops `PLAYER_RADIUS_M + target radius` short of an actor goal's centre,
 * so the strike reaches the goal's body exactly when its radius is at least the caster's own.
 */
export const DASH_RADIUS_MIN_M = PLAYER_RADIUS_M;

export type DashRefusalCode =
  | 'DASH_RANGE_INVALID'
  | 'DASH_RADIUS_TOO_SMALL'
  | 'DASH_APPLICATION_UNSUPPORTED'
  | 'DASH_SIMULTANEOUS_UNSUPPORTED'
  | 'DASH_SAME_TICK'
  | 'DASH_CHAIN_RANGE_EXCEEDS_DASH'
  | 'DASH_EVENT_POINT_UNPRICED';

export const DASH_REFUSAL_CODES: readonly DashRefusalCode[] = Object.freeze([
  'DASH_RANGE_INVALID', 'DASH_RADIUS_TOO_SMALL', 'DASH_APPLICATION_UNSUPPORTED', 'DASH_SIMULTANEOUS_UNSUPPORTED', 'DASH_SAME_TICK',
  'DASH_CHAIN_RANGE_EXCEEDS_DASH', 'DASH_EVENT_POINT_UNPRICED',
]);

/** True when a dash range is one a member may take. */
export function dashRangeIsValid(rangeM: number): boolean {
  return Number.isFinite(rangeM) && rangeM > 0 && rangeM <= DASH_ATTACK_RANGE_MAX_M;
}

/** True when a strike radius is guaranteed to reach an actor goal at an unshortened landing. */
export function dashRadiusIsValid(radiusM: number): boolean {
  return Number.isFinite(radiusM) && radiusM >= DASH_RADIUS_MIN_M;
}

/**
 * True when a dash chain's jump is one its dash always completes. A chain picks its next carrier within the jump of the node
 * it struck, as every chain does, but the caster stands a body gap short of that node; a jump of at most the dash's range is
 * within the dash's reach (`range` plus the next body's gap) from any such landing, so the pick is never one the dash cannot reach.
 */
export function dashChainJumpIsReachable(jumpRangeM: number, dashRangeM: number): boolean {
  return jumpRangeM <= dashRangeM;
}

/** The facts the same-tick rule reads of one stage, whether it comes from a canonical stage or a written program. */
export interface DashStageFacts {
  readonly stageId: string;
  readonly dash: boolean;
  /** Members of one volley (a chain's nodes). */
  readonly members: number;
  readonly memberSchedule: 'SIMULTANEOUS' | 'SEQUENTIAL' | 'STAGGERED';
  /** Ticks between two members of a volley; 0 unless `SEQUENTIAL`. */
  readonly memberIntervalTicks: number;
  readonly volleys: number;
  /** Ticks between two volleys; 0 for one volley. */
  readonly volleyIntervalTicks: number;
  readonly timing: StageTimingV1;
}

export interface DashRefusal {
  readonly stageId: string;
  readonly code: DashRefusalCode;
  readonly message: string;
}

/** The ticks one release of the stage spans, from its first member to its last. */
function releaseSpanTicks(stage: DashStageFacts): number {
  return (stage.volleys - 1) * stage.volleyIntervalTicks + (stage.members - 1) * stage.memberIntervalTicks;
}

/** The first and last tick of the stage's members when the program alone fixes them, else undefined. */
function fixedWindow(stage: DashStageFacts): readonly [number, number] | undefined {
  const start = stage.timing.kind === 'AT_RELEASE' ? 0 : stage.timing.kind === 'AFTER_RELEASE' ? stage.timing.offsetTicks : undefined;
  return start === undefined ? undefined : [start, start + releaseSpanTicks(stage)];
}

/** Whether `later` is released by the END per CAST of `earlier`, at least a tick after it: every member of `earlier` is out by then. */
function releasedAfterEndOf(later: DashStageFacts, earlier: DashStageFacts): boolean {
  const { timing } = later;
  return timing.kind === 'ON_STAGE_EVENT' && timing.referenceStageId === earlier.stageId && timing.event === 'END' && timing.per === 'CAST'
    && (timing.delayTicks ?? 0) >= 1;
}

/**
 * Every way two dash members of one cast can release on the same tick, from the timings alone and conservatively: members
 * of one volley released together, volleys that overlap, a stage released by an event that can happen more than once (or
 * at several carriers at once), and two dash stages whose release windows are not provably apart.
 */
export function dashScheduleRefusals(stages: readonly DashStageFacts[]): DashRefusal[] {
  const refusals: DashRefusal[] = [];
  const dashes = stages.filter((stage) => stage.dash);
  for (const stage of dashes) {
    const { timing } = stage;
    // A dash released at an event point has no exact price yet: whether the point is within the dash's reach of the caster is
    // a joint event with where the dash lands, and no term of the price carries it.
    if (timing.kind === 'ON_STAGE_EVENT' && timing.anchor === 'EVENT_POINT') {
      refusals.push({ stageId: stage.stageId, code: 'DASH_EVENT_POINT_UNPRICED', message: `stage ${stage.stageId} dashes to an event point: how far the point is from the caster, and so where the dash lands, has no exact price; anchor it at the caster` });
      continue;
    }
    if (stage.members > 1 && stage.memberSchedule !== 'SEQUENTIAL') {
      refusals.push({ stageId: stage.stageId, code: 'DASH_SIMULTANEOUS_UNSUPPORTED', message: `stage ${stage.stageId} releases ${stage.members} dash members together: the caster is one body, so its members go one after another (SEQUENTIAL)` });
      continue;
    }
    if (stage.volleys > 1 && stage.volleyIntervalTicks <= (stage.members - 1) * stage.memberIntervalTicks) {
      refusals.push({ stageId: stage.stageId, code: 'DASH_SAME_TICK', message: `stage ${stage.stageId} repeats every ${stage.volleyIntervalTicks} ticks, no longer than one volley's ${(stage.members - 1) * stage.memberIntervalTicks}: two of its dashes can move the caster on one tick` });
      continue;
    }
    const once = timing.kind !== 'ON_STATUS' && (timing.kind !== 'ON_STAGE_EVENT'
      || (timing.per === 'CAST' && timing.event !== 'EVERY_N' && timing.anchor !== 'MARKED' && timing.anchor !== 'CARRIERS'));
    if (!once) {
      refusals.push({ stageId: stage.stageId, code: 'DASH_SAME_TICK', message: `stage ${stage.stageId} is released by an event that can happen more than once, or at several enemies at once: two of its dashes can move the caster on one tick; release it once per cast` });
    }
  }
  for (let later = 1; later < dashes.length; later += 1) {
    for (let earlier = 0; earlier < later; earlier += 1) {
      const a = dashes[earlier]!;
      const b = dashes[later]!;
      if (releasedAfterEndOf(b, a) || releasedAfterEndOf(a, b)) continue;
      const wa = fixedWindow(a);
      const wb = fixedWindow(b);
      if (wa !== undefined && wb !== undefined && (wa[1] < wb[0] || wb[1] < wa[0])) continue;
      refusals.push({ stageId: b.stageId, code: 'DASH_SAME_TICK', message: `stages ${a.stageId} and ${b.stageId} both dash and their releases can fall on one tick; time them apart, or release ${b.stageId} at least a tick after the end of ${a.stageId}` });
    }
  }
  return refusals;
}
